import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { AppLayout } from '@/app/layout/components/app.layout';
import { AuthService } from '@/app/auth/auth.service';
import { authGuard, loginGuestGuard } from '@/app/auth/auth.guard';
import { permissionGuard } from '@/app/auth/permission.guard';
import { resolveRoleLanding } from '@/app/auth/role-landing';

export const appRoutes: Routes = [
    {
        path: 'login',
        canActivate: [loginGuestGuard],
        loadComponent: () => import('@/app/pages/login/login.component').then((c) => c.LoginComponent)
    },
    {
        path: '',
        component: AppLayout,
        canActivate: [authGuard],
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: () => resolveRoleLanding(inject(AuthService).currentUser()?.roles).defaultRoute
            },
            {
                path: 'triage',
                canActivate: [permissionGuard('quick-view:view')],
                data: { breadcrumb: 'Quick View', title: 'Quick View' },
                loadComponent: () => import('@/app/pages/overview/coordinator-overview.component').then((c) => c.CoordinatorOverviewComponent)
            },
            // Legacy path — /overview was the original route for this screen (Triage). Kept as a
            // redirect (mirrors catalog -> regimens from M2.5) so old bookmarks/links still resolve.
            { path: 'overview', redirectTo: 'triage', pathMatch: 'full' },
            {
                path: 'requests',
                data: { breadcrumb: 'Requests' },
                loadComponent: () => import('@/app/pages/requests/requests.component').then((c) => c.RequestsComponent)
            },
            {
                path: 'requests/new',
                loadComponent: () => import('@/app/pages/requests/new/new-request.component').then((c) => c.NewRequestComponent)
            },
            {
                path: 'requests/:id',
                loadComponent: () => import('@/app/pages/requests/detail/request-detail.component').then((c) => c.RequestDetailComponent)
            },
            {
                path: 'dashboard',
                data: { breadcrumb: 'Dashboard' },
                loadComponent: () => import('@/app/pages/dashboard/dashboard.component').then((c) => c.DashboardComponent)
            },
            {
                path: 'catalog',
                data: { breadcrumb: 'Catalog' },
                loadComponent: () => import('@/app/pages/protocol-catalog/protocol-catalog.component').then((c) => c.ProtocolCatalogComponent)
            },
            {
                path: 'regimens/:id',
                loadComponent: () => import('@/app/pages/regimens/regimen-detail/regimen-detail.component').then((c) => c.RegimenDetailComponent)
            },
            // Back-compat: the detail page moved from /catalog/:id to /regimens/:id. Preserve existing links.
            { path: 'catalog/:id', redirectTo: 'regimens/:id', pathMatch: 'full' },
            { path: 'protocol-catalog', redirectTo: '/catalog', pathMatch: 'full' },
            {
                path: 'worklist',
                data: { breadcrumb: 'Work Queue', title: 'Work Queue' },
                loadComponent: () => import('@/app/pages/worklist/worklist.component').then((c) => c.WorklistComponent)
            },
            {
                // Evidence Search / Review surface. Reached from the request journey stepper's
                // evidence_review stage as `?requestId=<id>`: shows the Pending long-poll screen until the
                // stage leaves Pending (real request state), then the evidence search. Also openable bare
                // (no requestId) as the standalone search surface. Not in nav.
                path: 'evidence-search',
                data: { breadcrumb: 'Evidence Search', title: 'Evidence Search' },
                loadComponent: () => import('@/app/pages/evidence-search-preview/evidence-search-preview.component').then((c) => c.EvidenceSearchPreviewComponent)
            },
            // Back-compat: the surface was renamed from /evidence-search-preview. Preserve existing links
            // (redirectTo carries the ?requestId query param through unchanged).
            { path: 'evidence-search-preview', redirectTo: 'evidence-search', pathMatch: 'full' },
            {
                path: 'sources',
                data: { breadcrumb: 'Sources', title: 'Sources' },
                loadComponent: () => import('@/app/pages/sources/sources.component').then((c) => c.SourcesComponent)
            },
            {
                path: 'sources/:id',
                data: { breadcrumb: 'Source', title: 'Source' },
                loadComponent: () => import('@/app/pages/sources/detail/source-detail.component').then((c) => c.SourceDetailComponent)
            },
            {
                path: 'treatment-protocols',
                data: { breadcrumb: 'Treatment Protocols', title: 'Treatment Protocols' },
                loadComponent: () => import('@/app/pages/treatment-protocols/treatment-protocols-list.component').then((c) => c.TreatmentProtocolsListComponent)
            },
            {
                path: 'treatment-protocols/:id',
                data: { breadcrumb: 'Treatment Protocol', title: 'Treatment Protocol' },
                loadComponent: () => import('@/app/pages/treatment-protocols/treatment-protocol-detail.component').then((c) => c.TreatmentProtocolDetailComponent)
            },
            {
                path: 'regimens',
                data: { breadcrumb: 'Regimens', title: 'Regimens' },
                loadComponent: () => import('@/app/pages/regimens/regimens.component').then((c) => c.RegimensComponent)
            },
            {
                path: 'order-sets',
                data: { breadcrumb: 'Order sets', title: 'Order sets' },
                loadComponent: () => import('@/app/pages/order-sets/order-sets-list.component').then((c) => c.OrderSetsListComponent)
            },
            {
                path: 'order-sets/:id',
                data: { breadcrumb: 'Order set', title: 'Order set' },
                loadComponent: () => import('@/app/pages/order-sets/order-set-detail.component').then((c) => c.OrderSetDetailComponent)
            },
            {
                path: 'site-scope',
                data: { breadcrumb: 'Site Scope', title: 'Site Scope' },
                loadComponent: () => import('@/app/pages/site-scope/site-scope.component').then((c) => c.SiteScopeComponent)
            },
            {
                // Isolated New Request engine sandbox — read-only, not in nav (reachable by URL only).
                path: 'sandbox/new-request',
                data: { breadcrumb: 'New Request Sandbox', title: 'New Request Sandbox' },
                loadComponent: () => import('@/app/pages/sandbox/new-request-sandbox.component').then((c) => c.NewRequestSandboxComponent)
            },
            {
                path: 'terminology',
                data: { breadcrumb: 'Terminology', title: 'Terminology' },
                loadComponent: () => import('@/app/pages/placeholder/placeholder.component').then((c) => c.PlaceholderComponent)
            },
            {
                path: 'compare',
                data: { breadcrumb: 'Compare' },
                loadComponent: () => import('@/app/pages/compare/compare.component').then((c) => c.CompareComponent)
            },
            {
                path: 'compare-review',
                data: { breadcrumb: 'Compare' },
                loadComponent: () => import('@/app/pages/compare-review/compare-review.component').then((c) => c.CompareReviewComponent)
            },
            {
                path: 'clinical-validation/:reviewerId',
                data: { breadcrumb: 'Clinical Validation' },
                loadComponent: () =>
                    import('@/app/pages/clinical-validation/clinical-validation-workspace.component').then(
                        (c) => c.ClinicalValidationWorkspaceComponent
                    )
            },
            {
                path: 'treatment-plan',
                data: { breadcrumb: 'Treatment plan' },
                loadComponent: () => import('@/app/pages/treatment-plan/treatment-plan.component').then((c) => c.TreatmentPlanComponent)
            },
            {
                path: 'extraction',
                data: { breadcrumb: 'Document Extraction' },
                loadComponent: () => import('@/app/pages/extraction/extraction.component').then((c) => c.ExtractionComponent)
            },
            {
                path: 'local-adaptations',
                data: { breadcrumb: 'Local adaptations' },
                loadComponent: () => import('@/app/pages/local-adaptations/local-adaptations.component').then((c) => c.LocalAdaptationsComponent)
            },
            {
                path: 'local-adaptations/ingest',
                data: { breadcrumb: 'Ingest artifact' },
                loadComponent: () => import('@/app/pages/local-adaptations/ingest/ingest-artifact.component').then((c) => c.IngestArtifactComponent)
            },
            {
                path: 'local-adaptations/new',
                data: { breadcrumb: 'New adaptation' },
                loadComponent: () => import('@/app/pages/local-adaptations/workspace/lra-workspace.component').then((c) => c.LraWorkspaceComponent)
            },
            {
                path: 'local-adaptations/:id',
                data: { breadcrumb: 'Workspace' },
                loadComponent: () => import('@/app/pages/local-adaptations/workspace/lra-workspace.component').then((c) => c.LraWorkspaceComponent)
            },
            {
                path: 'validation',
                data: { breadcrumb: 'Validation' },
                loadComponent: () => import('@/app/pages/cyan-blue/validation/validation.component').then((c) => c.ValidationComponent)
            },
            {
                path: 'approvals',
                data: { breadcrumb: 'Approvals' },
                loadComponent: () => import('@/app/pages/cyan-blue/approvals/approvals.component').then((c) => c.ApprovalsComponent)
            },
            {
                path: 'audit-log',
                data: { breadcrumb: 'Audit log' },
                loadComponent: () => import('@/app/pages/cyan-blue/audit-log/audit-log.component').then((c) => c.AuditLogComponent)
            },
            {
                path: 'settings',
                data: { breadcrumb: 'Settings' },
                children: [
                    {
                        path: 'tags',
                        data: { breadcrumb: 'Tag management' },
                        loadComponent: () => import('@/app/pages/settings/tags/tag-manager.component').then((c) => c.TagManagerComponent)
                    },
                    {
                        path: 'ai-gateway',
                        data: { breadcrumb: 'AI Gateway' },
                        loadChildren: () => import('@/app/pages/settings/ai-gateway/ai-gateway.routes').then((m) => m.aiGatewayRoutes)
                    },
                    {
                        path: 'users-groups',
                        data: { breadcrumb: 'Access Management' },
                        loadChildren: () => import('@/app/pages/settings/users-groups/users-groups.routes').then((m) => m.usersGroupsRoutes)
                    },
                    { path: '', redirectTo: 'tags', pathMatch: 'full' }
                ]
            },
            {
                path: 'workflows',
                data: { breadcrumb: 'Workflows' },
                loadComponent: () => import('@/app/pages/workflows/workflows-list.component').then((c) => c.WorkflowsListComponent)
            },
            {
                path: 'workflows/:id',
                data: { breadcrumb: 'Workflow' },
                loadComponent: () => import('@/app/pages/workflows/workflow-definition-detail.component').then((c) => c.WorkflowDefinitionDetailComponent)
            },
            {
                path: 'journey-templates',
                data: { breadcrumb: 'Journey templates' },
                loadComponent: () => import('@/app/pages/settings/journey-templates/journey-templates.component').then((c) => c.JourneyTemplatesComponent)
            },
            {
                path: 'governance',
                data: { breadcrumb: 'Governance' },
                loadComponent: () => import('@/app/pages/governance/governance.component').then((c) => c.GovernanceComponent)
            },
            {
                path: 'governance/setup',
                data: { breadcrumb: 'Configure review' },
                loadComponent: () => import('@/app/pages/governance/governance-setup.component').then((c) => c.GovernanceSetupComponent)
            },
            {
                path: 'governance/:id',
                data: { breadcrumb: 'Review' },
                loadComponent: () => import('@/app/pages/governance/governance-detail.component').then((c) => c.GovernanceDetailComponent)
            },
            {
                path: 'deployments/:id',
                data: { breadcrumb: 'Deployment' },
                loadComponent: () => import('@/app/pages/deployments/deployment-detail.component').then((c) => c.DeploymentDetailComponent)
            },
            {
                path: 'workflows-old',
                data: { breadcrumb: 'Workflows (legacy)' },
                loadComponent: () => import('@/app/pages/workflows-old/workflows.component').then((c) => c.WorkflowsComponent)
            },
            {
                path: 'workflows-old/:id',
                loadComponent: () => import('@/app/pages/workflows-old/detail/workflow-detail.component').then((c) => c.WorkflowDetailComponent)
            },
            {
                path: 'workflows-old/:id/activity',
                loadComponent: () => import('@/app/pages/workflows-old/detail/workflow-detail.component').then((c) => c.WorkflowDetailComponent)
            },
            {
                path: 'ingestion',
                data: { breadcrumb: 'Ingestion' },
                loadComponent: () => import('@/app/pages/ingestion/ingestion.component').then((c) => c.IngestionComponent)
            },
            {
                path: 'deployment',
                data: { breadcrumb: 'Deployment' },
                loadComponent: () => import('@/app/pages/deployment/deployment.component').then((c) => c.DeploymentComponent)
            },
            {
                path: 'adapters',
                data: { breadcrumb: 'Adapters' },
                loadComponent: () => import('@/app/pages/adapters/adapters.component').then((c) => c.AdaptersComponent)
            },
            {
                path: 'adapters/schema',
                data: { breadcrumb: 'CBCM Schema' },
                loadComponent: () => import('@/app/pages/adapters/schema/cbcm-schema.component').then((c) => c.CbcmSchemaComponent)
            },
            {
                path: 'adapters/:id/edit',
                data: { breadcrumb: 'Transformation Studio' },
                loadComponent: () => import('@/app/pages/adapters/studio/transformation-studio.component').then((c) => c.TransformationStudioComponent)
            },
            {
                path: 'adapters/:id/history',
                data: { breadcrumb: 'Version history' },
                loadComponent: () => import('@/app/pages/adapters/history/adapter-history.component').then((c) => c.AdapterHistoryComponent)
            },
            {
                path: 'integrations',
                data: { breadcrumb: 'Integrations' },
                loadComponent: () => import('@/app/pages/cyan-blue/integrations/integrations.component').then((c) => c.IntegrationsComponent)
            },
            {
                path: 'dashboards',
                data: { breadcrumb: 'Analytics Dashboard' },
                loadChildren: () => import('@/app/pages/dashboard/dashboard.routes')
            },
            {
                path: 'uikit',
                data: { breadcrumb: 'UI Kit' },
                loadChildren: () => import('@/app/pages/uikit/uikit.routes')
            },
            {
                path: 'documentation',
                data: { breadcrumb: 'Documentation' },
                loadComponent: () => import('@/app/pages/documentation/documentation').then((c) => c.Documentation)
            },
            {
                path: 'pages',
                data: { breadcrumb: 'Pages' },
                loadChildren: () => import('@/app/pages/pages.routes')
            },
            {
                path: 'apps',
                data: { breadcrumb: 'Apps' },
                loadChildren: () => import('./app/apps/apps.routes')
            },
            {
                path: 'ecommerce',
                data: { breadcrumb: 'E-Commerce' },
                loadChildren: () => import('@/app/pages/ecommerce/ecommerce.routes')
            },
            {
                path: 'blocks',
                data: { breadcrumb: 'Prime Blocks' },
                loadChildren: () => import('@/app/pages/blocks/blocks.routes')
            },
            {
                path: 'profile',
                data: { breadcrumb: 'User Management' },
                loadChildren: () => import('@/app/pages/usermanagement/usermanagement.routes')
            }
        ]
    },
    { path: 'auth', loadChildren: () => import('@/app/pages/auth/auth.routes') },
    {
        path: 'landing',
        loadComponent: () => import('@/app/pages/landing/landing').then((c) => c.Landing)
    },
    {
        path: 'notfound',
        loadComponent: () => import('@/app/pages/notfound/notfound').then((c) => c.Notfound)
    },
    { path: '**', redirectTo: '/notfound' }
];
